import React, { useState, useEffect, useCallback, useRef } from 'react';
import { collection, getDocs, query, where, doc, getDoc } from 'firebase/firestore';
import { MapPin, Users, Settings, ExternalLink, Search } from 'lucide-react';
import { isValidCoordinate } from '../utils/geolocation';

// Clé API Google Maps - sera chargée depuis les paramètres ou variables d'environnement
// Aucune clé en dur dans le code

const MapView = ({ db, currentUser }) => {
  const [clients, setClients] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [radius, setRadius] = useState(10);
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [mapLoaded, setMapLoaded] = useState(false);
  const [roadDistances, setRoadDistances] = useState({});
  const [apiKey, setApiKey] = useState(null);

  // Refs pour éviter les re-renders inutiles
  const mapRef = useRef(null);
  const mapElRef = useRef(null);
  const markersRef = useRef([]);
  const circleRef = useRef(null);
  const [mapInitialized, setMapInitialized] = useState(false);

  // Charger le script Google Maps (avec la lib "marker" pour AdvancedMarkerElement)
  useEffect(() => {
    // Ne pas charger le script si l'API n'est pas configurée
    if (!apiKey) {
      console.log('⏳ API non configurée, attente pour charger le script...');
      return;
    }

    const checkGoogleMapsLoaded = () => {
      // Vérification simple mais robuste
      return !!(window.google && 
                window.google.maps && 
                typeof window.google.maps.Map === 'function');
    };

    if (checkGoogleMapsLoaded()) {
      console.log('✅ Google Maps API déjà chargée');
      setMapLoaded(true);
      return;
    }

    const existing = document.querySelector('script[data-gmaps]');
    if (existing) {
      console.log('⚠️ Script Google Maps déjà présent, attente du chargement...');
      const iv = setInterval(() => {
        if (checkGoogleMapsLoaded()) {
          clearInterval(iv);
          console.log('✅ Google Maps API chargée');
          setMapLoaded(true);
        }
      }, 100);
      setTimeout(() => clearInterval(iv), 5000);
      return;
    }

    console.log('📦 Chargement du script Google Maps...');
    const script = document.createElement('script');
    script.setAttribute('data-gmaps', '1');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places,marker&v=weekly&loading=async`;
    script.async = true;
    script.defer = true;
    script.onload = () => {
      console.log('📦 Script chargé, vérification de l\'API...');
      const iv = setInterval(() => {
        if (checkGoogleMapsLoaded()) {
          clearInterval(iv);
          console.log('✅ Google Maps API prête');
          setMapLoaded(true);
        }
      }, 100);
      setTimeout(() => clearInterval(iv), 5000);
    };
    script.onerror = (e) => console.error('❌ Erreur chargement Google Maps:', e);
    document.head.appendChild(script);
  }, [apiKey]); // Dépendre de apiKey pour recharger si la clé change

  // Géocodage adresse → coords avec validation robuste
  const getCoordinatesFromAddress = async (address) => {
    // Validation robuste de l'adresse
    if (!address) {
      console.warn('❌ Adresse vide ou non définie');
      return null;
    }
    
    // Vérifier que l'adresse est une chaîne de caractères
    if (typeof address !== 'string') {
      console.warn('❌ Adresse n\'est pas une chaîne de caractères:', typeof address, address);
      return null;
    }
    
    // Nettoyer l'adresse
    const cleanAddress = address.trim();
    if (cleanAddress.length === 0) {
      console.warn('❌ Adresse vide après nettoyage');
      return null;
    }
    
    // Vérifier que l'adresse contient au moins un caractère alphanumérique
    if (!/[a-zA-Z0-9]/.test(cleanAddress)) {
      console.warn('❌ Adresse ne contient pas de caractères alphanumériques:', cleanAddress);
      return null;
    }

    if (!window.google || !window.google.maps || typeof window.google.maps.Geocoder !== 'function') {
      console.warn('Google Maps Geocoder non disponible');
      return null;
    }
    
    try {
      const geocoder = new window.google.maps.Geocoder();
      return new Promise((resolve, reject) => {
        geocoder.geocode({ address: cleanAddress }, (results, status) => {
          if (status === 'OK' && results[0]) {
            const loc = results[0].geometry.location;
            console.log(`📍 Géocodage réussi pour: "${cleanAddress}"`);
            resolve({ lat: loc.lat(), lng: loc.lng() });
          } else {
            console.warn(`❌ Géocodage échoué pour: "${cleanAddress}" - Status: ${status}`);
            reject(new Error(`Adresse non trouvée: ${status}`));
          }
        });
      });
    } catch (e) {
      console.error('Erreur géocodage:', e);
      return null;
    }
  };

  // Charger les clients
  const fetchClients = useCallback(async () => {
    if (!db || !mapLoaded) return; // Attendre que la carte soit chargée
    try {
      const clientsRef = collection(db, 'clients');
      const q = query(clientsRef, where('isActive', '==', true));
      const querySnapshot = await getDocs(q);

      console.log(`📋 Chargement de ${querySnapshot.size} clients actifs...`);

      const clientsWithCoords = await Promise.all(
        querySnapshot.docs.map(async (doc) => {
          const data = doc.data();
          let coordinates = null;
          
          // Gérer les différents formats d'adresse
          let addressString = '';
          if (typeof data.address === 'string') {
            addressString = data.address;
          } else if (data.address && typeof data.address === 'object') {
            // Si l'adresse est un objet, construire la chaîne
            const { street, city, postalCode, country } = data.address;
            addressString = [street, postalCode, city, country].filter(Boolean).join(', ');
          }
          
          if (addressString && mapLoaded) { // Vérifier que l'API est chargée
            try { 
              coordinates = await getCoordinatesFromAddress(addressString);
              console.log(`📍 Géocodage réussi pour: ${data.firstName} ${data.lastName}`);
            } catch (e) {
              console.log(`❌ Géocodage échoué pour: ${addressString}`);
            }
          }
          
          // Retourner le client avec l'adresse formatée
          return {
            id: doc.id,
            ...data,
            address: addressString, // Toujours une chaîne (évite de passer un objet au JSX)
            coordinates
          };
        })
      );

      const withCoords = clientsWithCoords.filter(c => c.coordinates);
      console.log(`✅ ${withCoords.length}/${clientsWithCoords.length} clients géolocalisés`);
      
      setClients(clientsWithCoords);
    } catch (e) {
      console.error('Erreur chargement clients:', e);
    }
  }, [db, mapLoaded]);

  // Charger les employés avec zone d'intervention par défaut
  const fetchEmployees = useCallback(async () => {
    if (!db || !mapLoaded) return; // Attendre que la carte soit chargée
    try {
      // Récupérer les employés non-archivés
      const employeesRef = collection(db, 'employees');
      const q = query(employeesRef, where('isArchived', '==', false));
      const querySnapshot = await getDocs(q);

      // Récupérer les IDs et emails des clients pour exclure les bénéficiaires
      const clientsSnap = await getDocs(collection(db, 'clients'));
      const clientIds = new Set(clientsSnap.docs.map(d => d.id));
      const clientEmails = new Set(
        clientsSnap.docs.map(d => d.data().email).filter(Boolean).map(e => e.toLowerCase())
      );

      const employeesWithCoords = await Promise.all(
        querySnapshot.docs
          .filter(doc => {
            const email = doc.data().email?.toLowerCase();
            return !clientIds.has(doc.id) && (!email || !clientEmails.has(email));
          })
          .map(async (doc) => {
            const data = doc.data();
            let coordinates = null;
            if (data.address?.street && mapLoaded) {
              const full = `${data.address.street}, ${data.address.postalCode} ${data.address.city}`;
              try { coordinates = await getCoordinatesFromAddress(full); } catch {}
            }

            // Zone d'intervention par défaut (10km si non définie)
            const defaultRadius = data.defaultRadius || 10;

            return { id: doc.id, ...data, coordinates, defaultRadius };
          })
      );
      setEmployees(employeesWithCoords);
    } catch (e) {
      console.error('Erreur chargement employés:', e);
    }
  }, [db, mapLoaded]);

  // Init carte une seule fois quand l'API est chargée - Version optimisée avec gestion d'erreur
  useEffect(() => {
    if (!mapLoaded || mapRef.current) return;

    let retryCount = 0;
    const maxRetries = 10; // Maximum 10 tentatives

    const initMap = () => {
      try {
        console.log(`🗺️ Tentative d'initialisation de la carte Google Maps (${retryCount + 1}/${maxRetries})...`);
        
        // Trouver l'élément map directement par ID
        const mapElement = document.getElementById('map');
        if (!mapElement) {
          console.log('⏳ Élément map non trouvé');
          if (retryCount < maxRetries) {
            retryCount++;
            setTimeout(initMap, 300);
          } else {
            console.error('❌ Élément map introuvable après plusieurs tentatives');
          }
          return;
        }

        // Vérifier que l'élément est visible et a des dimensions
        if (mapElement.offsetWidth === 0 || mapElement.offsetHeight === 0) {
          console.log('⏳ Élément map pas encore visible');
          if (retryCount < maxRetries) {
            retryCount++;
            setTimeout(initMap, 300);
          } else {
            console.error('❌ Élément map reste invisible après plusieurs tentatives');
          }
          return;
        }

        console.log('✅ Élément map trouvé et visible, initialisation...');
        
        // Défaut: France métropolitaine
        const fallbackCenter = { lat: 46.7111, lng: 1.7191 };
        mapRef.current = new window.google.maps.Map(mapElement, {
          center: fallbackCenter,
          zoom: 6,
          styles: [
            { featureType: 'poi', elementType: 'labels', stylers: [{ visibility: 'off' }] },
          ],
          mapId: 'DEMO_MAP_ID',
          gestureHandling: 'greedy', // Meilleure gestion des gestes sur mobile
        });
        console.log('✅ Carte Google Maps initialisée avec succès');
        setMapInitialized(true);
      } catch (error) {
        console.error('❌ Erreur lors de l\'initialisation de la carte:', error);
        if (retryCount < maxRetries) {
          retryCount++;
          console.log(`🔄 Nouvelle tentative dans 500ms... (${retryCount}/${maxRetries})`);
          setTimeout(initMap, 500);
        } else {
          console.error('❌ Échec de l\'initialisation de la carte après plusieurs tentatives');
        }
      }
    };

    // Démarrer l'initialisation avec un délai initial
    setTimeout(initMap, 100);
  }, [mapLoaded]);

  // Fonction pour créer un marqueur avec AdvancedMarkerElement ou fallback
  const createClientMarker = (map, client) => {
    const info = new window.google.maps.InfoWindow({
      content: `
        <div style="padding:8px;max-width:250px">
          <h3 style="margin:0 0 8px 0;color:#1f2937;font-weight:700;font-size:16px">${client.firstName || ''} ${client.lastName || ''}</h3>
          ${client.address ? `<p style="margin:0 0 6px 0;color:#6b7280;font-size:14px">📍 ${typeof client.address === 'string' ? client.address : `${client.address?.street || ''} ${client.address?.postalCode || ''} ${client.address?.city || ''}`.trim()}</p>` : ''}
          ${client.phoneNumber ? `<p style="margin:0;color:#6b7280;font-size:14px">📞 ${client.phoneNumber}</p>` : ''}
          <div style="margin-top:8px;display:flex;flex-direction:column;gap:4px">
            ${client.address ? `<button onclick="window.open('https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(client.address)}','_blank')" style="background:#2563eb;color:#fff;border:none;padding:6px 12px;border-radius:4px;cursor:pointer;font-size:12px">Voir sur Maps</button>` : ''}
            <button onclick="window.open('/client-details/${client.id}', '_blank')" style="background:#10b981;color:#fff;border:none;padding:6px 12px;border-radius:4px;cursor:pointer;font-size:12px">Voir fiche client</button>
          </div>
        </div>
      `,
    });

    // Couleur par défaut ou couleur du client
    const clientColor = client.color || '#dc2626'; // Rouge par défaut si pas de couleur

    if (window.google?.maps?.marker?.AdvancedMarkerElement) {
      // ✅ Advanced Marker avec couleur du client
      const content = document.createElement('div');
      content.innerHTML = `
        <div style="
          background:${clientColor};width:28px;height:28px;border-radius:50%;
          border:2px solid #fff;box-shadow:0 2px 8px rgba(0,0,0,.25);
          display:flex;align-items:center;justify-content:center;color:#fff;font-size:14px;
        ">📍</div>
      `;

      const marker = new window.google.maps.marker.AdvancedMarkerElement({
        position: client.coordinates,
        map,
        title: `${client.firstName || ''} ${client.lastName || ''}`.trim(),
        content,
      });

      // ℹ️ AdvancedMarkerElement déclenche 'gmp-click'
      window.google.maps.event.addListener(marker, 'gmp-click', () => {
        // Fermer toutes les autres infoWindows avant d'ouvrir celle-ci
        markersRef.current.forEach(m => {
          if (m.infoWindow && m.infoWindow !== info) {
            m.infoWindow.close();
          }
        });
        info.open({ map, anchor: marker });
      });

      // Stocker l'infoWindow avec le marqueur pour pouvoir la fermer plus tard
      marker.infoWindow = info;
      return marker;
    }

    // 🔙 Fallback Marker classique
    const marker = new window.google.maps.Marker({
      position: client.coordinates,
      map,
      title: `${client.firstName || ''} ${client.lastName || ''}`.trim(),
      icon: {
        url: 'https://maps.google.com/mapfiles/ms/icons/red-dot.png',
        scaledSize: new window.google.maps.Size(32, 32),
      },
    });

    // Écouteur de clic simple pour les marqueurs classiques
    marker.addListener('click', () => {
      // Fermer toutes les autres infoWindows avant d'ouvrir celle-ci
      markersRef.current.forEach(m => {
        if (m.infoWindow && m.infoWindow !== info) {
          m.infoWindow.close();
        }
      });
      info.open({ map, anchor: marker });
    });

    // Stocker l'infoWindow avec le marqueur pour pouvoir la fermer plus tard
    marker.infoWindow = info;
    return marker;
  };

  // Pose (et repose) les marqueurs clients quand la liste change OU quand la carte est initialisée
  useEffect(() => {
    if (!mapRef.current) {
      console.log('⏳ Carte non encore initialisée, attente pour les marqueurs...');
      return;
    }

    if (clients.length === 0) {
      console.log('⏳ Aucun client à placer sur la carte...');
      return;
    }

    console.log(`🗺️ Placement des marqueurs pour ${clients.length} clients...`);
    
    // Nettoyage anciens marqueurs
    markersRef.current.forEach((m) => {
      if (m?.setMap) m.setMap(null);
    });
    markersRef.current = [];

    const map = mapRef.current;
    const withCoords = clients.filter((c) => !!c.coordinates);

    console.log(`📍 ${withCoords.length} clients avec coordonnées géographiques`);

    if (withCoords.length === 0) {
      console.log('ℹ️ Aucun client avec coordonnées à afficher');
      return;
    }

    const bounds = new window.google.maps.LatLngBounds();

    withCoords.forEach((client) => {
      const marker = createClientMarker(map, client);
      
      // Pour le fitBounds, utiliser directement les coordonnées (AdvancedMarker n'a pas getPosition())
      bounds.extend(client.coordinates);

      markersRef.current.push(marker);
    });

    // Ajuster la vue pour voir tous les marqueurs
    if (withCoords.length > 0) {
      map.fitBounds(bounds);
      if (map.getZoom() > 16) map.setZoom(16);
      console.log(`✅ ${withCoords.length} marqueurs placés sur la carte`);
    }
  }, [clients, mapInitialized]); // Utiliser mapInitialized comme déclencheur

  // Cercles collaborateurs (multiple uniquement)
  useEffect(() => {
    if (!mapRef.current) return;
    const map = mapRef.current;
    
    // Supprimer tous les anciens cercles
    if (circleRef.current) {
      circleRef.current.forEach(circle => circle.setMap(null));
      circleRef.current = [];
    }

    // Créer des cercles pour chaque employé sélectionné
    const circles = [];
    selectedEmployees.forEach((employee) => {
      if (employee?.coordinates) {
        // Utiliser la couleur de l'employé ou une couleur par défaut
        const color = employee.color || '#dc2626'; // Rouge par défaut
        
        const circle = new window.google.maps.Circle({
          strokeColor: color,
          strokeOpacity: 0.8,
          strokeWeight: 2,
          fillColor: color,
          fillOpacity: 0.08,
          map,
          center: employee.coordinates,
          radius: (employee.defaultRadius || radius) * 1000,
        });
        circles.push(circle);
      }
    });

    circleRef.current = circles;

    // Centrer la carte sur le premier employé sélectionné si disponible
    if (selectedEmployees.length > 0 && selectedEmployees[0]?.coordinates) {
      map.setCenter(selectedEmployees[0].coordinates);
      map.setZoom(10);
    }
  }, [selectedEmployees, radius]);

  // Charger la clé API depuis Firestore
  useEffect(() => {
    const loadApiKey = async () => {
      try {
        const settingsDoc = await getDoc(doc(db, 'settings', 'general'));
        if (settingsDoc.exists()) {
          const data = settingsDoc.data();
          if (data.googleMapsApiKey) {
            setApiKey(data.googleMapsApiKey);
          }
        }
      } catch (error) {
        console.error('Erreur lors du chargement de la clé API:', error);
      }
    };

    if (db) {
      loadApiKey();
    }
  }, [db]);

  useEffect(() => {
    const load = async () => {
      setIsLoading(true);
      await Promise.all([fetchClients(), fetchEmployees()]);
      setIsLoading(false);
    };
    load();
  }, [fetchClients, fetchEmployees]);

  // Debug: Vérifier les données quand elles changent
  useEffect(() => {
    console.log('🔍 Données clients mises à jour:', clients);
    console.log('🔍 Données employés mises à jour:', employees);
  }, [clients, employees]);

  const openGoogleMaps = (address) => {
    window.open(`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`, '_blank');
  };

  const calculateRoadDistance = async (fromLat, fromLng, toLat, toLng) => {
    if (!window.google?.maps?.DirectionsService) {
      console.warn('Google Maps DirectionsService non disponible');
      return null;
    }
    
    try {
      const directionsService = new window.google.maps.DirectionsService();
      
      return new Promise((resolve) => {
        directionsService.route(
          {
            origin: new window.google.maps.LatLng(fromLat, fromLng),
            destination: new window.google.maps.LatLng(toLat, toLng),
            travelMode: window.google.maps.TravelMode.DRIVING,
            // Forcer l'itinéraire le plus court en évitant autoroutes et péages
            // Cela donne généralement un itinéraire plus direct et plus court
            avoidHighways: true,
            avoidTolls: true,
            provideRouteAlternatives: false,
            optimizeWaypoints: false
          },
          (response, status) => {
            if (status === 'OK' && response.routes[0]?.legs[0]?.distance) {
              // Google Maps retourne maintenant un itinéraire plus court
              const distance = response.routes[0].legs[0].distance.value / 1000; // Convertir en km
              resolve(Math.round(distance * 10) / 10);
            } else {
              console.warn('Erreur calcul distance routière:', status);
              resolve(null);
            }
          }
        );
      });
    } catch (error) {
      console.error('Erreur calcul distance routière:', error);
      return null;
    }
  };

  const openGoogleMapsDirections = (fromAddress, toAddress) => {
    // Forcer l'itinéraire le plus court en évitant autoroutes et péages
    window.open(`https://www.google.com/maps/dir/?api=1&origin=${encodeURIComponent(fromAddress)}&destination=${encodeURIComponent(toAddress)}&avoid=highways|tolls`, '_blank');
  };

  const getEmployeeAddress = (employee) => {
    if (!employee?.address) return '';
    const { street, postalCode, city } = employee.address;
    return `${street}, ${postalCode} ${city}`;
  };

  // Fonction spécifique pour MapView qui utilise l'API Google Maps Geometry si disponible
  const calculateDistance = (lat1, lon1, lat2, lon2) => {
    // Vérification des coordonnées valides
    if (!lat1 || !lon1 || !lat2 || !lon2) return 0;
    if (Math.abs(lat1) > 90 || Math.abs(lat2) > 90 || Math.abs(lon1) > 180 || Math.abs(lon2) > 180) return 0;
    
    // Utiliser l'API Google Maps Geometry si disponible pour une précision maximale
    if (window.google?.maps?.geometry?.spherical) {
      try {
        const from = new window.google.maps.LatLng(lat1, lon1);
        const to = new window.google.maps.LatLng(lat2, lon2);
        const distance = window.google.maps.geometry.spherical.computeDistanceBetween(from, to) / 1000;
        return Math.round(distance * 10) / 10; // Arrondir à 1 décimale
      } catch (error) {
        console.warn('Erreur API Google Maps Geometry, utilisation de Haversine:', error);
      }
    }
    
    // Fallback: Formule Haversine (précision ~99.5%)
    const R = 6371; // Rayon moyen de la Terre en km
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    
    const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
              Math.cos(lat1 * Math.PI/180) * Math.cos(lat2 * Math.PI/180) * 
              Math.sin(dLon/2) * Math.sin(dLon/2);
    
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    const distance = R * c;
    
    // Retourner avec 1 décimale de précision
    return Math.round(distance * 10) / 10;
  };


  // Fonction pour calculer les distances entre un client et les employés sélectionnés
  const calculateClientDistances = (client) => {
    if (!client.coordinates || selectedEmployees.length === 0) return {};
    
    const distances = {};
    selectedEmployees.forEach(employee => {
      if (employee.coordinates) {
        const distance = calculateDistance(
          employee.coordinates.lat,
          employee.coordinates.lng,
          client.coordinates.lat,
          client.coordinates.lng
        );
        distances[employee.id] = Math.round(distance * 10) / 10; // Arrondir à 1 décimale
      }
    });
    return distances;
  };

  const filteredClients = clients.filter((client) => {
    const matchesSearch = (
      client.lastName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      client.firstName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      client.address?.toLowerCase().includes(searchTerm.toLowerCase())
    );
    
    // Si des employés sont sélectionnés, filtrer par distance
    if (selectedEmployees.length > 0 && client.coordinates) {
      const isInAnyZone = selectedEmployees.some(employee => {
        if (!employee.coordinates) return false;
        const distance = calculateDistance(
          employee.coordinates.lat,
          employee.coordinates.lng,
          client.coordinates.lat,
          client.coordinates.lng
        );
        // Utiliser le rayon ajusté pour le filtrage
        return distance <= radius;
      });
      return matchesSearch && isInAnyZone;
    }
    
    return matchesSearch;
  });

  const handleMultipleEmployeeSelect = (employeeIds) => {
    const selected = employees.filter((e) => employeeIds.includes(e.id));
    setSelectedEmployees(selected);
  };

  // Calculer les distances routières quand la sélection change - avec gestion d'erreur améliorée
  useEffect(() => {
    const calculateAllRoadDistances = async () => {
      if (selectedEmployees.length === 0 || clients.length === 0) {
        setRoadDistances({});
        return;
      }

      // Vérifier si Directions API est disponible
      if (!window.google?.maps?.DirectionsService) {
        console.warn('Directions Service non disponible - API peut-être non autorisée');
        return;
      }

      const newRoadDistances = {};
      
      // Utiliser les clients filtrés mais sans créer de dépendance cyclique
      const clientsToProcess = clients.filter(client => 
        client.coordinates && 
        (searchTerm ? 
          (client.lastName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
           client.firstName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
           client.address?.toLowerCase().includes(searchTerm.toLowerCase())) : true
        ) &&
        (selectedEmployees.length > 0 ? 
          selectedEmployees.some(employee => {
            if (!employee.coordinates) return false;
            const distance = calculateDistance(
              employee.coordinates.lat,
              employee.coordinates.lng,
              client.coordinates.lat,
              client.coordinates.lng
            );
            return distance <= radius; // Utiliser le rayon ajusté pour le filtrage
          }) : true
        )
      );

      // Limiter le nombre de calculs pour éviter les dépassements de quota
      const maxCalculations = 20; // Limite pour éviter trop d'appels API
      let calculationCount = 0;

      for (const client of clientsToProcess) {
        if (!client.coordinates) continue;
        
        for (const employee of selectedEmployees) {
          if (!employee.coordinates) continue;
          
          const key = `${employee.id}-${client.id}`;
          // Ne pas recalculer si déjà présent
          if (roadDistances[key]) continue;

          // Limiter le nombre de calculs
          if (calculationCount >= maxCalculations) {
            console.log('⚠️ Limite de calculs atteinte pour éviter les dépassements de quota');
            break;
          }
          
          try {
            const distance = await calculateRoadDistance(
              employee.coordinates.lat,
              employee.coordinates.lng,
              client.coordinates.lat,
              client.coordinates.lng
            );
            
            calculationCount++;
            
            if (distance !== null) {
              newRoadDistances[key] = distance;
            }
          } catch (error) {
            console.warn('Erreur lors du calcul de distance routière:', error);
            // Continuer avec les autres calculs malgré l'erreur
          }
        }
        
        if (calculationCount >= maxCalculations) break;
      }
      
      setRoadDistances(prev => ({ ...prev, ...newRoadDistances }));
    };

    calculateAllRoadDistances();
  }, [selectedEmployees, clients, searchTerm, radius]); // Éviter filteredClients dans les dépendances

  // Si l'API n'est pas configurée, afficher le guide complet
  if (!apiKey) {
    return (
      <div className="bg-white dark:bg-gray-800 p-6 rounded-lg border border-gray-200 dark:border-gray-700">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-semibold text-gray-800 dark:text-gray-100">Carte des bénéficiaires</h2>
        </div>

        {/* Guide complet de configuration */}
        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-6">
          <div className="flex items-center mb-6">
            <Settings size={24} className="text-blue-600 dark:text-blue-400 mr-3" />
            <h3 className="text-xl font-semibold text-gray-800 dark:text-gray-100">Guide complet de configuration Google Maps API</h3>
          </div>

          <div className="space-y-6">
            {/* Étape 1 */}
            <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg">
              <h4 className="font-semibold text-blue-800 dark:text-blue-300 mb-2">1. Créer un projet Google Cloud</h4>
              <ul className="text-sm text-blue-700 dark:text-blue-400 space-y-1">
                <li>• Allez sur <a href="https://console.cloud.google.com/" target="_blank" rel="noopener noreferrer" className="underline">Google Cloud Console</a></li>
                <li>• Créez un nouveau projet ou sélectionnez un projet existant</li>
                <li>• Activez la facturation (nécessaire pour les APIs Google Maps)</li>
              </ul>
            </div>

            {/* Étape 2 */}
            <div className="bg-green-50 dark:bg-green-900/20 p-4 rounded-lg">
              <h4 className="font-semibold text-green-800 dark:text-green-300 mb-2">2. Activer les APIs nécessaires</h4>
              <ul className="text-sm text-green-700 dark:text-green-400 space-y-1">
                <li>• <strong>Maps JavaScript API</strong> - Pour l'affichage des cartes</li>
                <li>• <strong>Geocoding API</strong> - Pour convertir les adresses en coordonnées</li>
                <li>• <strong>Directions API</strong> - Pour calculer les distances routières</li>
                <li>• <strong>Places API</strong> - Pour la recherche d'adresses</li>
              </ul>
            </div>

            {/* Étape 3 */}
            <div className="bg-purple-50 dark:bg-purple-900/20 p-4 rounded-lg">
              <h4 className="font-semibold text-purple-800 dark:text-purple-300 mb-2">3. Créer une clé API</h4>
              <ul className="text-sm text-purple-700 dark:text-purple-400 space-y-1">
                <li>• Allez dans "APIs et Services" → "Identifiants"</li>
                <li>• Cliquez sur "Créer des identifiants" → "Clé API"</li>
                <li>• Copiez la clé générée</li>
              </ul>
            </div>

            {/* Étape 4 */}
            <div className="bg-orange-50 dark:bg-orange-900/20 p-4 rounded-lg">
              <h4 className="font-semibold text-orange-800 dark:text-orange-300 mb-2">4. Configurer les restrictions</h4>
              <ul className="text-sm text-orange-700 dark:text-orange-400 space-y-1">
                <li>• <strong>Restrictions HTTP</strong> : Ajoutez votre domaine (localhost pour le développement)</li>
                <li>• <strong>Restrictions d'API</strong> : Restreignez aux APIs Maps, Geocoding, Directions, Places</li>
                <li>• Configurez des quotas pour éviter les dépassements de coûts</li>
              </ul>
            </div>

            {/* Étape 5 */}
            <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg">
              <h4 className="font-semibold text-blue-800 dark:text-blue-300 mb-2">5. Configurer dans l'application</h4>
              <ol className="text-sm text-blue-700 dark:text-blue-400 space-y-2 list-decimal list-inside">
                <li>Allez dans <strong>Paramètres → Général</strong></li>
                <li>Renseignez votre clé API Google Maps dans la section dédiée</li>
                <li>Sauvegardez les modifications</li>
                <li>Rechargez cette page pour activer les fonctionnalités</li>
              </ol>
            </div>

            {/* Sécurité */}
            <div className="bg-red-50 dark:bg-red-900/20 p-4 rounded-lg">
              <h4 className="font-semibold text-red-800 dark:text-red-300 mb-2">⚠️ Sécurité importante</h4>
              <ul className="text-sm text-red-700 dark:text-red-400 space-y-1">
                <li>• <strong>NE JAMAIS</strong> committer la clé API dans le code versionné</li>
                <li>• Restreindre la clé à votre domaine de production</li>
                <li>• Configurer des quotas et alertes de budget</li>
                <li>• Utiliser différentes clés pour développement et production</li>
              </ul>
            </div>

            {/* Liens utiles */}
            <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
              <h4 className="font-semibold text-gray-800 dark:text-gray-200 mb-2">🔗 Liens utiles</h4>
              <ul className="text-sm text-gray-700 dark:text-gray-300 space-y-1">
                <li>• <a href="https://console.cloud.google.com/" target="_blank" rel="noopener noreferrer" className="underline">Google Cloud Console</a></li>
                <li>• <a href="https://developers.google.com/maps" target="_blank" rel="noopener noreferrer" className="underline">Documentation Google Maps</a></li>
                <li>• <a href="https://cloud.google.com/support" target="_blank" rel="noopener noreferrer" className="underline">Support Google Cloud</a></li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-96">
        <div className="text-lg text-gray-900 dark:text-gray-100">Chargement des données...</div>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-800 p-6 rounded-lg border border-gray-200 dark:border-gray-700">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-semibold text-gray-800 dark:text-gray-100">Carte des bénéficiaires</h2>
      </div>

      {/* Carte - Version optimisée desktop */}
      {mapLoaded && (
        <div className="mb-6">
          <div 
            id="map" 
            style={{ 
              height: '500px', 
              width: '100%', 
              borderRadius: '8px', 
              border: '1px solid #e5e7eb',
              minHeight: '400px'
            }} 
            className="mb-2" 
          />
          <div className="text-xs text-gray-600 dark:text-gray-400 text-center">
            {selectedEmployees.length > 0 ? `${selectedEmployees.length} cercle(s) affiché(s) autour des collaborateurs sélectionnés.` :
             'Sélectionne un ou plusieurs collaborateurs pour afficher les cercles de couverture.'}
            <br />
            <span className="text-gray-500 dark:text-gray-400">⚡ = Distance à vol d'oiseau (cliquez pour itinéraire routier)</span>
          </div>
        </div>
      )}

      {/* Info API - Affichage conditionnel selon l'état de configuration */}
      {apiKey ? (
        <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-700 p-3 rounded-lg mb-4">
          <p className="text-sm text-green-700 dark:text-green-400">✅ API Google Maps configurée - Géolocalisation active</p>
        </div>
      ) : (
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 p-4 rounded-lg mb-4">
          <div className="flex items-start">
            <div className="flex-shrink-0">
              <Settings size={20} className="text-blue-600 dark:text-blue-400 mt-0.5" />
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-blue-800 dark:text-blue-300">Configuration requise</h3>
              <div className="mt-2 text-sm text-blue-700 dark:text-blue-400">
                <p>Pour activer la géolocalisation et les calculs d'itinéraires :</p>
                <ol className="list-decimal list-inside mt-2 space-y-1">
                  <li>Allez dans <strong>Paramètres → Général</strong></li>
                  <li>Renseignez votre clé API Google Maps</li>
                  <li>Sauvegardez les modifications</li>
                  <li>Rechargez cette page pour activer les fonctionnalités</li>
                </ol>
                <div className="mt-3 p-3 bg-blue-100 dark:bg-blue-800/30 rounded-md">
                  <p className="text-xs font-medium text-blue-800 dark:text-blue-300">ℹ️ Documentation complète disponible :</p>
                  <p className="text-xs text-blue-700 dark:text-blue-400">
                    Consultez le guide "GUIDE_CONFIGURATION_API_GOOGLE_MAPS.md" pour les instructions détaillées de configuration.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Recherche */}
      <div className="mb-4 relative">
        <input
          type="text"
          placeholder="Rechercher un bénéficiaire..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full p-3 pl-10 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400"
        />
        <Search className="absolute left-3 top-3.5 text-gray-400 dark:text-gray-400" size={18} />
      </div>

      {/* Sélection collaborateur - Multiple uniquement */}
      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Sélectionner un ou plusieurs collaborateurs:</label>
        <select
          multiple
          value={selectedEmployees.map(e => e.id)}
          onChange={(e) => handleMultipleEmployeeSelect(Array.from(e.target.selectedOptions, option => option.value))}
          className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 h-32 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
        >
          {employees.map((e) => (
            <option key={e.id} value={e.id}>
              {e.firstName} {e.lastName} {e.defaultRadius ? `(${e.defaultRadius} km)` : ''}
            </option>
          ))}
        </select>
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
          Maintenez Ctrl (Windows) ou Cmd (Mac) pour sélectionner plusieurs collaborateurs
        </p>
      </div>

      {/* Ajustement du rayon - Optionnel */}
      <div className="mb-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 p-4 rounded-lg">
        <h4 className="text-sm font-medium text-blue-800 dark:text-blue-300 mb-2">🔧 Ajustement temporaire du rayon</h4>
        <p className="text-xs text-blue-600 dark:text-blue-400 mb-3">
          Ce réglage ajuste temporairement le rayon d'affichage sans modifier la valeur par défaut des collaborateurs.
        </p>
        <div className="flex items-center space-x-2">
          <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Rayon d'affichage:</label>
          <select
            value={radius}
            onChange={(e) => setRadius(Number(e.target.value))}
            className="border border-gray-300 dark:border-gray-600 rounded px-3 py-1.5 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value={5}>5 km</option>
            <option value={10}>10 km</option>
            <option value={20}>20 km</option>
            <option value={30}>30 km</option>
            <option value={50}>50 km</option>
          </select>
          <span className="text-xs text-gray-500 dark:text-gray-400">(affichage seulement)</span>
        </div>
      </div>

      {/* Liste des bénéficiaires - Optimisé desktop */}
      <div className="mb-6">
        <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100 mb-4">
          {selectedEmployees.length > 0 ?
            `Bénéficiaires dans les zones sélectionnées (${filteredClients.length})` :
            `Tous les bénéficiaires (${filteredClients.length})`}
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
          {filteredClients.map((client) => (
            <div key={client.id} className="bg-white dark:bg-gray-700 p-4 rounded-lg border border-gray-200 dark:border-gray-600 shadow-sm hover:shadow-md transition-shadow">
              <div className="flex items-start justify-between">
                <div className="flex items-start flex-grow">
                  <MapPin size={16} className="mt-1 mr-2 flex-shrink-0" style={{ color: client.color || '#dc2626' }} />
                  <div className="flex-grow">
                    <p className="font-medium text-gray-900 dark:text-gray-100">{client.lastName} {client.firstName}</p>
                    {client.address && (<p className="text-sm text-gray-600 dark:text-gray-300 mt-1">{client.address}</p>)}
                    {client.phoneNumber && (<p className="text-sm text-gray-600 dark:text-gray-300 mt-1">{client.phoneNumber}</p>)}
                  </div>
                </div>
                {client.address && (
                  <button onClick={() => openGoogleMaps(client.address)} className="ml-2 text-gray-400 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors" title="Ouvrir dans Google Maps">
                    <ExternalLink size={16} />
                  </button>
                )}
              </div>

              {/* Boutons d'itinéraire avec distance vers chaque employé sélectionné */}
              {selectedEmployees.length > 0 && client.address && (
                <div className="mt-4 space-y-2">
                  {selectedEmployees.map((employee) => {
                    if (!employee.address) return null;

                    // Calculer la distance pour cet employé
                    const distance = calculateClientDistances(client)[employee.id];

                    return (
                      <div key={employee.id} className="flex items-center justify-between bg-gray-50 dark:bg-gray-600 p-2 rounded text-xs">
                        <span className="font-medium text-gray-700 dark:text-gray-200">{employee.firstName}</span>
                        <div className="flex items-center space-x-2">
                          <div className="flex flex-col items-end">
                            {roadDistances[`${employee.id}-${client.id}`] ? (
                              <>
                                <span className="text-gray-800 dark:text-gray-100 font-semibold" title="Distance par la route">
                                  {roadDistances[`${employee.id}-${client.id}`]} km 🛣️
                                </span>
                                <span className="text-xs text-gray-500 dark:text-gray-400" title="Distance à vol d'oiseau">
                                  {distance} km ⚡
                                </span>
                              </>
                            ) : (
                              <span className="text-gray-600 dark:text-gray-300 font-medium" title="Distance à vol d'oiseau">
                                {distance} km ⚡
                              </span>
                            )}
                          </div>
                          <button
                            onClick={() => openGoogleMapsDirections(getEmployeeAddress(employee), client.address)}
                            className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 transition-colors"
                            title={`Itinéraire depuis ${employee.firstName} ${employee.lastName}`}
                          >
                            <ExternalLink size={14} />
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          ))}
        </div>
        {filteredClients.length === 0 && (
          <p className="text-gray-500 dark:text-gray-400 text-center py-6">
            {searchTerm ? 'Aucun bénéficiaire trouvé avec cette recherche' :
             selectedEmployees.length > 0 ? 'Aucun bénéficiaire dans les zones sélectionnées' :
             'Aucun bénéficiaire trouvé'}
          </p>
        )}
      </div>

      {/* Statistiques */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white dark:bg-gray-700 p-4 rounded-lg border border-gray-200 dark:border-gray-600 text-center">
          <Users size={20} className="mx-auto text-gray-600 dark:text-gray-300 mb-2" />
          <p className="text-xl font-semibold text-gray-800 dark:text-gray-100">{employees.length}</p>
          <p className="text-sm text-gray-600 dark:text-gray-300">Collaborateurs</p>
        </div>
        <div className="bg-white dark:bg-gray-700 p-4 rounded-lg border border-gray-200 dark:border-gray-600 text-center">
          <MapPin size={20} className="mx-auto text-gray-600 dark:text-gray-300 mb-2" />
          <p className="text-xl font-semibold text-gray-800 dark:text-gray-100">{clients.length}</p>
          <p className="text-sm text-gray-600 dark:text-gray-300">Bénéficiaires</p>
        </div>
        <div className="bg-white dark:bg-gray-700 p-4 rounded-lg border border-gray-200 dark:border-gray-600 text-center">
          <Settings size={20} className="mx-auto text-gray-600 dark:text-gray-300 mb-2" />
          <p className="text-xl font-semibold text-gray-800 dark:text-gray-100">{radius}</p>
          <p className="text-sm text-gray-600 dark:text-gray-300">km de rayon</p>
        </div>
      </div>
    </div>
  );
};

export default MapView;
